import "./newTravel.scss";
import Sidebar from "../../components/sidebar/Sidebar";
import Navbar from "../../components/navbar/Navbar";
import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import {
  DriveFolderUploadOutlined as UploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
} from "@mui/icons-material";
import {
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  CircularProgress,
  Paper,
  Grid,
  Typography,
  IconButton,
  Chip,
  Box,
  Divider,
} from "@mui/material";
import { useSelector } from "react-redux";

const NewTravel = ({ inputs, title }) => {
  const userIdRedux = useSelector((state) => state.user.userId);
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();
  const { state } = useLocation();
  const travelData = state?.travel || {};
  const isEditMode = !!travelData._id;

  // State for form data
  const [formData, setFormData] = useState({
    userId: travelData.userId || userIdRedux,
    travelName: travelData.travelName || "",
    travelCategory: travelData.travelCategory || "Cascade",
    travelGuidePhone: travelData.travelGuidePhone || "",
    travelPrice: travelData.travelPrice || "0",
    travelPerson: travelData.travelPerson || "",
    travelDuration: travelData.travelDuration || "",
    travelAddress: travelData.travelAddress || "",
    travelDescription: travelData.travelDescription || "",
  });

  // Separate state for images
  const [existingImages, setExistingImages] = useState([]);
  const [newImageFiles, setNewImageFiles] = useState([]);
  const [newImagePreviews, setNewImagePreviews] = useState([]);
  const [imagesToDelete, setImagesToDelete] = useState([]);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [backEndError, setBackEndError] = useState("");

  // Load existing images when in edit mode
  useEffect(() => {
    if (isEditMode && travelData.travelImages) {
      // Handle both string and array formats
      const images = Array.isArray(travelData.travelImages)
        ? travelData.travelImages
        : [travelData.travelImages];

      setExistingImages(images.filter((img) => img)); // Remove empty values
    }
  }, [isEditMode, travelData.travelImages]);

  // Clean up preview URLs on unmount
  useEffect(() => {
    return () => {
      newImagePreviews.forEach((preview) => {
        if (preview.startsWith("blob:")) {
          URL.revokeObjectURL(preview);
        }
      });
    };
  }, [newImagePreviews]);

  const validateForm = () => {
    const errors = {};

    if (!formData.travelName) errors.travelName = "Name is required.";
    if (!formData.travelPrice) errors.travelPrice = "Expense is required.";
    if (!formData.travelPerson) errors.travelPerson = "Person is required.";
    if (!formData.travelDuration)
      errors.travelDuration = "Duration is required.";
    if (!formData.travelAddress) errors.travelAddress = "Address is required.";

    if (!formData.travelGuidePhone)
      errors.travelGuidePhone = "Phone number is required.";
    if (!/^\d{10}$/.test(formData.travelGuidePhone))
      errors.travelGuidePhone = "Phone number must be 10 digits.";

    if (!formData.travelDescription)
      errors.travelDescription = "Description is required.";

    // Image validation
    const totalImages = existingImages.length + newImageFiles.length;

    if (!isEditMode) {
      if (totalImages === 0) {
        errors.travelImages = "At least 3 images must be uploaded.";
      } else if (totalImages < 3) {
        errors.travelImages = `Please upload ${3 - totalImages} more image(s). Total required: 3`;
      } else if (totalImages > 3) {
        errors.travelImages = "You can only upload exactly 3 images.";
      }
    } else {
      // In edit mode, allow updates but maintain minimum
      if (totalImages === 0) {
        errors.travelImages = "At least one image is required.";
      }
      // Don't enforce exactly 3 in edit mode to allow partial updates
    }

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);

    // Check total images after adding new ones
    const totalAfterAdd =
      existingImages.length + newImageFiles.length + files.length;

    if (!isEditMode && totalAfterAdd > 3) {
      alert(
        `You can only upload exactly 3 images total. You currently have ${existingImages.length + newImageFiles.length} image(s).`,
      );
      return;
    }

    const validImages = files.filter((file) => {
      if (!file.type.startsWith("image/")) {
        alert(`File "${file.name}" is not a valid image.`);
        return false;
      }
      if (file.size > 5 * 1024 * 1024) {
        // 5MB limit
        alert(`File "${file.name}" exceeds the 5MB size limit.`);
        return false;
      }
      return true;
    });

    if (validImages.length > 0) {
      // Add to new image files
      setNewImageFiles((prev) => [...prev, ...validImages]);

      // Generate and add previews
      const previews = validImages.map((file) => URL.createObjectURL(file));
      setNewImagePreviews((prev) => [...prev, ...previews]);
    }
  };

  const handleRemoveExistingImage = (indexToRemove) => {
    const imageToRemove = existingImages[indexToRemove];
    setImagesToDelete((prev) => [...prev, imageToRemove]);
    setExistingImages((prev) =>
      prev.filter((_, index) => index !== indexToRemove),
    );
  };

  const handleRemoveNewImage = (indexToRemove) => {
    // Revoke the blob URL to prevent memory leaks
    if (newImagePreviews[indexToRemove]?.startsWith("blob:")) {
      URL.revokeObjectURL(newImagePreviews[indexToRemove]);
    }

    setNewImageFiles((prev) =>
      prev.filter((_, index) => index !== indexToRemove),
    );
    setNewImagePreviews((prev) =>
      prev.filter((_, index) => index !== indexToRemove),
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setBackEndError("");

    const formDataToSubmit = new FormData();

    // Append form fields
    Object.keys(formData).forEach((key) => {
      if (formData[key] !== undefined && formData[key] !== null) {
        formDataToSubmit.append(key, formData[key]);
      }
    });

    // Append new image files
    newImageFiles.forEach((file) => {
      formDataToSubmit.append("travelImages", file);
    });

    // If in edit mode and there are images to delete, send their URLs
    if (isEditMode && imagesToDelete.length > 0) {
      formDataToSubmit.append("imagesToDelete", JSON.stringify(imagesToDelete));
    }

    // Append existing images that are kept (for reference)
    if (isEditMode && existingImages.length > 0) {
      formDataToSubmit.append("keepImages", JSON.stringify(existingImages));
    }

    const MAIN_URL = isEditMode
      ? `${BASE_URL}/api/travel/updateTravel/${travelData._id}`
      : `${BASE_URL}/api/travel/addTravel`;

    try {
      const response = await axios({
        method: isEditMode ? "put" : "post",
        url: MAIN_URL,
        data: formDataToSubmit,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status === 201 || response.status === 200) {
        // Clean up blob URLs
        newImagePreviews.forEach((preview) => {
          if (preview.startsWith("blob:")) {
            URL.revokeObjectURL(preview);
          }
        });
        navigate("/travel");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.msg || "An error occurred";
      setBackEndError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="newUser">
      <Sidebar />
      <div className="newUserContainer">
        <Navbar />
        <div
          className="top"
          style={{ display: "flex", alignItems: "center", gap: "1rem" }}
        >
          <h1>{isEditMode ? "Edit Travel" : title}</h1>
          {isEditMode && (
            <Chip
              label={`ID: ${travelData._id}`}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </div>
        <div className="bottom">
          <form onSubmit={handleSubmit}>
            <Paper
              elevation={3}
              style={{ padding: "2rem", marginBottom: "2rem" }}
            >
              <Typography variant="h6" gutterBottom>
                <ImageIcon
                  style={{ marginRight: "0.5rem", verticalAlign: "middle" }}
                />
                Travel Images
              </Typography>
              <Divider style={{ marginBottom: "1.5rem" }} />

              {/* Image Upload Section */}
              <Box mb={3}>
                <input
                  id="travelImages"
                  name="travelImages"
                  type="file"
                  multiple
                  hidden
                  accept="image/*"
                  onChange={handleFileChange}
                />
                <label htmlFor="travelImages">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<UploadIcon />}
                    style={{ marginBottom: "1rem" }}
                  >
                    {isEditMode ? "Add More Images" : "Upload Images"}
                  </Button>
                </label>

                <Typography
                  variant="caption"
                  display="block"
                  color="textSecondary"
                >
                  {isEditMode
                    ? "You can add new images. Existing images will be preserved unless removed."
                    : "Please upload exactly 3 images (max 5MB each)"}
                </Typography>
              </Box>

              {/* Existing Images (Edit Mode) */}
              {isEditMode && existingImages.length > 0 && (
                <Box mb={3}>
                  <Typography variant="subtitle2" gutterBottom>
                    Existing Images:
                  </Typography>
                  <Grid container spacing={2}>
                    {existingImages.map((image, index) => (
                      <Grid item key={`existing-${index}`}>
                        <Box position="relative">
                          <img
                            src={image}
                            alt={`Existing ${index + 1}`}
                            style={{
                              width: "120px",
                              height: "120px",
                              objectFit: "cover",
                              borderRadius: "8px",
                              border: "2px solid #e0e0e0",
                            }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src =
                                "https://icon-library.com/images/no-image-icon/no-image-icon-0.jpg";
                            }}
                          />
                          <IconButton
                            size="small"
                            style={{
                              position: "absolute",
                              top: -8,
                              right: -8,
                              backgroundColor: "#ff4444",
                              color: "white",
                              padding: "4px",
                            }}
                            onClick={() => handleRemoveExistingImage(index)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                          <Chip
                            label="Existing"
                            size="small"
                            style={{
                              position: "absolute",
                              bottom: -8,
                              left: "50%",
                              transform: "translateX(-50%)",
                              backgroundColor: "#4caf50",
                              color: "white",
                              fontSize: "0.7rem",
                            }}
                          />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* New Image Previews */}
              {newImagePreviews.length > 0 && (
                <Box mb={3}>
                  <Typography variant="subtitle2" gutterBottom>
                    New Images to Upload:
                  </Typography>
                  <Grid container spacing={2}>
                    {newImagePreviews.map((preview, index) => (
                      <Grid item key={`new-${index}`}>
                        <Box position="relative">
                          <img
                            src={preview}
                            alt={`New ${index + 1}`}
                            style={{
                              width: "120px",
                              height: "120px",
                              objectFit: "cover",
                              borderRadius: "8px",
                              border: "2px solid #2196f3",
                            }}
                          />
                          <IconButton
                            size="small"
                            style={{
                              position: "absolute",
                              top: -8,
                              right: -8,
                              backgroundColor: "#ff4444",
                              color: "white",
                              padding: "4px",
                            }}
                            onClick={() => handleRemoveNewImage(index)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                          <Chip
                            label="New"
                            size="small"
                            style={{
                              position: "absolute",
                              bottom: -8,
                              left: "50%",
                              transform: "translateX(-50%)",
                              backgroundColor: "#2196f3",
                              color: "white",
                              fontSize: "0.7rem",
                            }}
                          />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Image Count Info */}
              <Box mt={2}>
                <Typography variant="body2" color="textSecondary">
                  Total Images: {existingImages.length + newImageFiles.length}
                  {isEditMode && (
                    <span style={{ marginLeft: "1rem", color: "#666" }}>
                      (Existing: {existingImages.length} | New:{" "}
                      {newImageFiles.length})
                    </span>
                  )}
                </Typography>
                {errors.travelImages && (
                  <Typography color="error" variant="caption">
                    {errors.travelImages}
                  </Typography>
                )}
              </Box>
            </Paper>

            <Paper elevation={3} style={{ padding: "2rem" }}>
              <Typography variant="h6" gutterBottom>
                Travel Details
              </Typography>
              <Divider style={{ marginBottom: "1.5rem" }} />

              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    name="travelName"
                    label="Travel Name *"
                    value={formData.travelName}
                    onChange={handleChange}
                    error={Boolean(errors.travelName)}
                    helperText={errors.travelName}
                    required
                  />
                </Grid>

                <Grid item xs={6}>
                  <FormControl fullWidth error={Boolean(errors.travelCategory)}>
                    <InputLabel>Category *</InputLabel>
                    <Select
                      name="travelCategory"
                      value={formData.travelCategory}
                      onChange={handleChange}
                    >
                      <MenuItem value="Cascade">Cascade</MenuItem>
                      <MenuItem value="Mountains">Mountains</MenuItem>
                      <MenuItem value="Camping">Camping</MenuItem>
                      <MenuItem value="Lakes">Lakes</MenuItem>
                      <MenuItem value="Historical Sites">
                        Historical Sites
                      </MenuItem>
                      <MenuItem value="National Parks">National Parks</MenuItem>
                      <MenuItem value="Religious Sites">
                        Religious Sites
                      </MenuItem>
                    </Select>
                    {errors.travelCategory && (
                      <FormHelperText>{errors.travelCategory}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    name="travelDuration"
                    label="Duration *"
                    value={formData.travelDuration}
                    onChange={handleChange}
                    error={Boolean(errors.travelDuration)}
                    helperText={errors.travelDuration}
                    placeholder="e.g., 3 days, 2 nights"
                    required
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    name="travelPerson"
                    label="Person *"
                    value={formData.travelPerson}
                    onChange={handleChange}
                    error={Boolean(errors.travelPerson)}
                    helperText={errors.travelPerson}
                    placeholder="e.g., 2 persons"
                    required
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    name="travelPrice"
                    label="Expense (NPR) *"
                    type="number"
                    value={formData.travelPrice}
                    onChange={handleChange}
                    error={Boolean(errors.travelPrice)}
                    helperText={errors.travelPrice}
                    required
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    name="travelAddress"
                    label="Address *"
                    value={formData.travelAddress}
                    onChange={handleChange}
                    error={Boolean(errors.travelAddress)}
                    helperText={errors.travelAddress}
                    required
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    name="travelGuidePhone"
                    label="Phone Number *"
                    value={formData.travelGuidePhone}
                    onChange={handleChange}
                    error={Boolean(errors.travelGuidePhone)}
                    helperText={errors.travelGuidePhone}
                    type="tel"
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="travelDescription"
                    label="Description *"
                    value={formData.travelDescription}
                    onChange={handleChange}
                    multiline
                    rows={4}
                    error={Boolean(errors.travelDescription)}
                    helperText={errors.travelDescription}
                    required
                  />
                </Grid>
              </Grid>
            </Paper>

            {backEndError && (
              <Typography color="error" style={{ marginTop: "1rem" }}>
                {backEndError}
              </Typography>
            )}

            <Box mt={3} display="flex" justifyContent="flex-start" gap={2}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                style={{ minWidth: "120px" }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : isEditMode ? (
                  "Update"
                ) : (
                  "Save"
                )}
              </Button>

              <Button variant="outlined" onClick={() => navigate("/travel")}>
                Cancel
              </Button>
            </Box>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NewTravel;
